import { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartContext';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { addItem, getTotalItems } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleAddToCart = (product: { name: string; price: string; img: string }, id: string) => {
    addItem({
      id,
      name: product.name,
      price: parseFloat(product.price),
      image: product.img,
    });
  };

  const goToCart = () => {
    window.REACT_APP_NAVIGATE('/carrinho');
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-lg'}`}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                alt="Gil Lanches" 
                className="h-10 md:h-12 w-auto object-contain" 
                src="https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/1712801b92aafb12988341f46925da22.jpeg"
              />
              <div className="hidden md:block">
                <h1 className="text-lg font-bold text-[#2D303D]">Gil Lanches</h1>
                <p className="text-xs text-gray-600">Delivery de Qualidade</p>
              </div>
            </div>
            <button 
              onClick={goToCart}
              className="relative bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer whitespace-nowrap" 
              aria-label="Carrinho de compras"
            >
              <i className="ri-shopping-cart-2-fill text-xl"></i>
              {getTotalItems() > 0 && (
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center animate-pulse">
                  {getTotalItems()}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[65vh] flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF9F1C] via-[#FFB84D] to-[#FFC966] opacity-90"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1000ms' }}></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white w-full">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Gil Lanches</h1>
            <p className="text-xl md:text-2xl mb-3 font-semibold">Sabor que Conquista! 🍔</p>
            <p className="text-base md:text-lg mb-6 text-white/90">Hambúrgueres, Pastéis, Bolos e muito mais!</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <button 
                onClick={() => scrollToSection('hamburgueres-especiais')}
                className="bg-white text-[#FF9F1C] hover:bg-white/90 font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer text-sm"
              >
                Ver Cardápio
              </button>
              <a 
                href="https://wa.me/5511965753001?text=Olá!%20Gostaria%20de%20fazer%20um%20pedido." 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-whatsapp-fill text-xl"></i>
                <span>Fale Conosco</span>
              </a>
            </div>
            <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4">
                <i className="ri-timer-flash-line text-2xl md:text-3xl mb-1"></i>
                <p className="text-xs md:text-sm font-semibold">Entrega Rápida</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4">
                <i className="ri-restaurant-line text-2xl md:text-3xl mb-1"></i>
                <p className="text-xs md:text-sm font-semibold">Feito na Hora</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4">
                <i className="ri-star-smile-line text-2xl md:text-3xl mb-1"></i>
                <p className="text-xs md:text-sm font-semibold">Qualidade</p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F5F5F5] to-transparent"></div>
      </section>

      {/* Navegação do Cardápio */}
      <nav className="bg-white shadow-md sticky top-[72px] z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <button onClick={() => scrollToSection('hamburgueres-especiais')} className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer text-xs md:text-sm transition-all duration-300 hover:scale-105">
              🍔 Lanches
            </button>
            <button onClick={() => scrollToSection('combos-de-lanches')} className="bg-[#FFB84D] hover:bg-[#FFB84D]/90 text-white font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer text-xs md:text-sm transition-all duration-300 hover:scale-105">
              🍟 Combos
            </button>
            <button onClick={() => scrollToSection('pasteis-fritos')} className="bg-[#FFC966] hover:bg-[#FFC966]/90 text-white font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer text-xs md:text-sm transition-all duration-300 hover:scale-105">
              🥟 Pastéis
            </button>
            <button onClick={() => scrollToSection('batata-grande-recheada')} className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer text-xs md:text-sm transition-all duration-300 hover:scale-105">
              🥔 Porções
            </button>
            <button onClick={() => scrollToSection('bolos-caseiros')} className="bg-[#FFB84D] hover:bg-[#FFB84D]/90 text-white font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer text-xs md:text-sm transition-all duration-300 hover:scale-105">
              🧁 Bolos
            </button>
            <button onClick={() => scrollToSection('bebidas-geladinhas')} className="bg-[#FFC966] hover:bg-[#FFC966]/90 text-white font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer text-xs md:text-sm transition-all duration-300 hover:scale-105">
              🥤 Bebidas
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* Hambúrgueres Especiais */}
        <section className="py-6" id="hamburgueres-especiais">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 bg-[#FF9F1C] rounded-lg flex items-center justify-center text-2xl">🍔</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D303D]">Hambúrgueres Especiais</h2>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { name: 'X-Tudo', desc: 'Hambúrguer, presunto, muçarela, ovo, bacon e salada', price: '25.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/3df18ad8cdb1a64eda4e6b6de1189329.png', badge: 'Popular' },
              { name: 'X-Bacon', desc: 'Hambúrguer, presunto, muçarela, bacon e salada', price: '23.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/8cfc7fecdcafd2c0c81f47789aa5217f.png' },
              { name: 'X-Salada', desc: 'Hambúrguer, presunto, muçarela e salada', price: '22.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/526349d9eee6084848bea4e1345763af.png' },
              { name: 'X-Egg', desc: 'Hambúrguer, presunto, muçarela, ovo e salada', price: '20.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/cc674acadcd893db66b26c1ca9b95561.jpeg' },
              { name: 'X-Burguer', desc: 'Hambúrguer, presunto, muçarela', price: '17.00', img: 'https://public.readdy.ai/ai/img_res/0090ac40809402b37119f7c420871ca1.jpg' }
            ].map((product, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative overflow-hidden h-32 md:h-40">
                  <img 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                    src={product.img}
                  />
                  {product.badge && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-[#2D303D] mb-1 text-sm md:text-base line-clamp-1">{product.name}</h3>
                  <p className="text-gray-600 text-xs mb-2 line-clamp-2">{product.desc}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-lg md:text-xl font-bold text-[#FF6B35]">R$ {product.price}</span>
                    <button 
                      onClick={() => handleAddToCart(product, `hamburger-${index}`)}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 whitespace-nowrap cursor-pointer text-xs"
                    >
                      <i className="ri-shopping-cart-line text-sm"></i>
                      <span>Pedir</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Combos de Lanches */}
        <section className="py-6" id="combos-de-lanches">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 bg-[#FFB84D] rounded-lg flex items-center justify-center text-2xl">🍟</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D303D]">Combos de Lanches</h2>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { name: 'Combo - X Tudo Completo', desc: 'Acompanha batata frita e Coca-Cola 350ml', price: '38.00', img: 'https://readdy.ai/api/search-image?query=Delicious%20complete%20burger%20combo%20with%20loaded%20hamburger%2C%20golden%20crispy%20french%20fries%20and%20Coca-Cola%20can%20on%20clean%20white%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20warm%20lighting%2C%20high%20quality%20detailed%20commercial%20shot&width=400&height=300&seq=combo001&orientation=landscape', badge: 'Popular' },
              { name: 'Combo - X Burguer', desc: 'Acompanha batata frita e Coca-Cola 350ml', price: '30.00', img: 'https://readdy.ai/api/search-image?query=Classic%20cheeseburger%20combo%20meal%20with%20beef%20patty%20burger%2C%20golden%20french%20fries%20and%20Coca-Cola%20can%20on%20clean%20white%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20warm%20lighting%2C%20high%20quality%20detailed%20commercial%20shot&width=400&height=300&seq=combo002&orientation=landscape' },
              { name: 'Combo - X Salada', desc: 'Acompanha batata frita e Coca-Cola 350ml', price: '35.00', img: 'https://readdy.ai/api/search-image?query=Fresh%20salad%20burger%20combo%20with%20lettuce%20tomato%20burger%2C%20crispy%20golden%20french%20fries%20and%20Coca-Cola%20can%20on%20clean%20white%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20warm%20lighting%2C%20high%20quality%20detailed%20commercial%20shot&width=400&height=300&seq=combo003&orientation=landscape' },
              { name: 'Combo - X Bacon', desc: 'Acompanha batata frita e Coca-Cola 350ml', price: '36.00', img: 'https://readdy.ai/api/search-image?query=Bacon%20cheeseburger%20combo%20meal%20with%20crispy%20bacon%20strips%20burger%2C%20golden%20french%20fries%20and%20Coca-Cola%20can%20on%20clean%20white%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20warm%20lighting%2C%20high%20quality%20detailed%20commercial%20shot&width=400&height=300&seq=combo004&orientation=landscape' },
              { name: 'Combo - X Egg', desc: 'Acompanha batata frita e Coca-Cola 350ml', price: '33.00', img: 'https://readdy.ai/api/search-image?query=Egg%20burger%20combo%20meal%20with%20fried%20egg%20hamburger%2C%20golden%20crispy%20french%20fries%20and%20Coca-Cola%20can%20on%20clean%20white%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20warm%20lighting%2C%20high%20quality%20detailed%20commercial%20shot&width=400&height=300&seq=combo005&orientation=landscape' }
            ].map((product, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative overflow-hidden h-32 md:h-40">
                  <img 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                    src={product.img}
                  />
                  {product.badge && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-[#2D303D] mb-1 text-sm md:text-base line-clamp-1">{product.name}</h3>
                  <p className="text-gray-600 text-xs mb-2 line-clamp-2">{product.desc}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-lg md:text-xl font-bold text-[#FF6B35]">R$ {product.price}</span>
                    <button 
                      onClick={() => handleAddToCart(product, `combo-${index}`)}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 whitespace-nowrap cursor-pointer text-xs"
                    >
                      <i className="ri-shopping-cart-line text-sm"></i>
                      <span>Pedir</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pastéis Fritos */}
        <section className="py-6" id="pasteis-fritos">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 bg-[#FFC966] rounded-lg flex items-center justify-center text-2xl">🥟</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D303D]">Pastéis Fritos</h2>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { name: 'Pastel de Carne', price: '14.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/730c705895a24bfdbed0fb8dc4e8df72.png' },
              { name: 'Pastel de Carne com Queijo', price: '15.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/fa585f5ded34fdfa61adac69af345280.jpeg', badge: 'Novo' },
              { name: 'Pastel de Frango com Catupiry', price: '15.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/73a09c491259c5be4266a8ca59db2c8b.jpeg' },
              { name: 'Pastel de Pizza', price: '15.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/5afd7bb58c0cbbd3afbe49349aaf19b9.png' },
              { name: 'Pastel de Queijo', price: '12.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/447ce71ee94b9bf6bfd355d0199a8716.jpeg' },
              { name: 'Mini Pastéis Fritos', desc: '25 unidades', price: '50.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/758b9a0ba48c3d3fb3399e0e10320ba1.jpeg', badge: 'Festa' }
            ].map((product, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative overflow-hidden h-32 md:h-40">
                  <img 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                    src={product.img}
                  />
                  {product.badge && (
                    <div className={`absolute top-2 right-2 ${product.badge === 'Novo' ? 'bg-green-500' : 'bg-purple-500'} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-[#2D303D] mb-1 text-sm md:text-base line-clamp-1">{product.name}</h3>
                  {product.desc && <p className="text-gray-600 text-xs mb-2">{product.desc}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-lg md:text-xl font-bold text-[#FF6B35]">R$ {product.price}</span>
                    <button 
                      onClick={() => handleAddToCart(product, `pastel-${index}`)}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 whitespace-nowrap cursor-pointer text-xs"
                    >
                      <i className="ri-shopping-cart-line text-sm"></i>
                      <span>Pedir</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Batata Grande Recheada */}
        <section className="py-6" id="batata-grande-recheada">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 bg-[#FF9F1C] rounded-lg flex items-center justify-center text-2xl">🥔</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D303D]">Porções</h2>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
              <div className="relative overflow-hidden h-32 md:h-40">
                <img 
                  alt="Batata Grande Recheada" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                  src="https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/7a43cc2ae8f450b301f750efade9aa1d.png"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-3">
                <h3 className="font-bold text-[#2D303D] mb-1 text-sm md:text-base line-clamp-1">Batata Grande Recheada</h3>
                <p className="text-gray-600 text-xs mb-2 line-clamp-2">Bacon, calabresa, queijo ralado, catupiry, cheddar, maionese e ketchup</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-lg md:text-xl font-bold text-[#FF6B35]">R$ 25.00</span>
                  <button 
                    onClick={() => handleAddToCart({ name: 'Batata Grande Recheada', price: '25.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/7a43cc2ae8f450b301f750efade9aa1d.png' }, 'batata-1')}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 whitespace-nowrap cursor-pointer text-xs"
                  >
                    <i className="ri-shopping-cart-line text-sm"></i>
                    <span>Pedir</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bolos Caseiros */}
        <section className="py-6" id="bolos-caseiros">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 bg-[#FFB84D] rounded-lg flex items-center justify-center text-2xl">🧁</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D303D]">Bolos Caseiros</h2>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { name: 'Bolo de Milho', price: '40.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/efb95361ba946334f8993425ec168464.jpeg', badge: 'Popular' },
              { name: 'Bolo de Chocolate', price: '40.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/29e69a914af0df479153b46935fae60f.jpeg' },
              { name: 'Bolo Vulcão de Cenoura com Chocolate', desc: 'Bolo de cenoura com recheio de chocolate', price: '85.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/211395b943b7b398f0a26a136ea0191c.jpeg', badge: 'Novo' },
              { name: 'Bolo de Laranja com Calda', price: '50.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/dccd7f7553b264ae9ae719141ae49ab6.jpeg' },
              { name: 'Bolo de Coco molhadinho com coco', desc: 'Bolo úmido de coco com cobertura de coco ralado', price: '55.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/77d4e10fb953d568c246460c252d1788.jpeg' },
              { name: 'Bolo Vulcão de Maracujá com Ninho', desc: 'Bolo de maracujá com recheio de leite ninho', price: '85.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/6d25371ecd585434881875a87a8c0541.jpeg', badge: 'Novo' },
              { name: 'Bolo com recheio de Abacaxi', desc: 'Bolo caseiro com recheio de abacaxi', price: '60.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/e44541bf34614b160c24c0d4772d94c4.jpeg' },
              { name: 'Torta de Frango com Catupiry', desc: '27cm x 37cm', price: '85.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/807206cfdc3f9b7e10c60b399fc31b67.jpeg' }
            ].map((product, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative overflow-hidden h-32 md:h-40">
                  <img 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                    src={product.img}
                  />
                  {product.badge && (
                    <div className={`absolute top-2 right-2 ${product.badge === 'Popular' ? 'bg-red-500' : 'bg-green-500'} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-[#2D303D] mb-1 text-sm md:text-base line-clamp-1">{product.name}</h3>
                  {product.desc && <p className="text-gray-600 text-xs mb-2 line-clamp-2">{product.desc}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-lg md:text-xl font-bold text-[#FF6B35]">R$ {product.price}</span>
                    <button 
                      onClick={() => handleAddToCart(product, `bolo-${index}`)}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 whitespace-nowrap cursor-pointer text-xs"
                    >
                      <i className="ri-shopping-cart-line text-sm"></i>
                      <span>Pedir</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Mini Salgados Variados */}
        <section className="py-6" id="mini-salgados-variados">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 bg-[#FFC966] rounded-lg flex items-center justify-center text-2xl">🍢</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D303D]">Mini Salgados Variados</h2>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { name: 'Mini Salgados - 25 unidades', price: '26.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/417bee1deed4d2b838cc8d7f4d37c5ea.png' },
              { name: 'Mini Salgados - 50 unidades', price: '48.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/417bee1deed4d2b838cc8d7f4d37c5ea.png', badge: 'Festa' }
            ].map((product, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative overflow-hidden h-32 md:h-40">
                  <img 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                    src={product.img}
                  />
                  {product.badge && (
                    <div className="absolute top-2 right-2 bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-[#2D303D] mb-1 text-sm md:text-base line-clamp-1">{product.name}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-lg md:text-xl font-bold text-[#FF6B35]">R$ {product.price}</span>
                    <button 
                      onClick={() => handleAddToCart(product, `salgado-${index}`)}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 whitespace-nowrap cursor-pointer text-xs"
                    >
                      <i className="ri-shopping-cart-line text-sm"></i>
                      <span>Pedir</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Bebidas Geladinhas */}
        <section className="py-6" id="bebidas-geladinhas">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 bg-[#FF9F1C] rounded-lg flex items-center justify-center text-2xl">🥤</div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2D303D]">Bebidas Geladinhas</h2>
          </div>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              { name: 'Coca-Cola 2,5L', price: '16.00', img: 'https://static.readdy.ai/image/c55ab1005bc1f9d4664d3099a061f682/cb9be3cba2fe632cc1d281c56ddfc7a5.png' },
              { name: 'Fanta Uva 2,5L', price: '16.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/0bc32857a77c2bfd84dcbeceb637c373.png' },
              { name: 'Coca-Cola Lata', price: '6.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/fcd77c468ac5d817f81d314633cdc7a3.png' },
              { name: 'Coca-Cola 600ml', price: '10.00', img: 'https://static.readdy.ai/image/3a137ec63759587ff533dcea41b4ea00/397c10b5532a882e4486111ad4be853b.png' }
            ].map((product, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative overflow-hidden h-32 md:h-40">
                  <img 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                    src={product.img}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-[#2D303D] mb-1 text-sm md:text-base line-clamp-1">{product.name}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-lg md:text-xl font-bold text-[#FF6B35]">R$ {product.price}</span>
                    <button 
                      onClick={() => handleAddToCart(product, `bebida-${index}`)}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 whitespace-nowrap cursor-pointer text-xs"
                    >
                      <i className="ri-shopping-cart-line text-sm"></i>
                      <span>Pedir</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#2D303D] via-gray-900 to-[#2D303D] text-white mt-12">
        <div className="container mx-auto px-4 py-10">
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div>
              <h3 className="text-xl font-bold mb-3 text-[#FF9F1C]">Contato</h3>
              <div className="space-y-2">
                <a href="tel:+5511965753001" className="flex items-center gap-2 hover:text-[#FF9F1C] transition-colors cursor-pointer text-sm">
                  <i className="ri-phone-fill text-lg"></i>
                  <span>(11) 96575-3001</span>
                </a>
                <a href="https://wa.me/5511965753001" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#FF9F1C] transition-colors cursor-pointer text-sm">
                  <i className="ri-whatsapp-fill text-lg"></i>
                  <span>WhatsApp</span>
                </a>
                <a href="https://instagram.com/gildecidesouzasilva" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#FF9F1C] transition-colors cursor-pointer text-sm">
                  <i className="ri-instagram-fill text-lg"></i>
                  <span>@gildecidesouzasilva</span>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3 text-[#FF9F1C]">Endereço</h3>
              <div className="flex items-start gap-2">
                <i className="ri-map-pin-fill text-lg text-[#FF9F1C] mt-1"></i>
                <p className="text-sm">Rua Julio Diniz, 223<br />Parque Piratininga<br />Itaquaquecetuba/SP</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3 text-[#FF9F1C]">Entrega</h3>
              <div className="flex items-start gap-2">
                <i className="ri-truck-fill text-lg text-[#FF9F1C] mt-1"></i>
                <p className="text-sm">Entregamos em:<br />• Itaquaquecetuba<br />• Guarulhos<br />• Arujá</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <i className="ri-qr-code-line text-[#FF9F1C]"></i>
                  Pix
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-bank-card-line text-[#FF9F1C]"></i>
                  Cartão
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-money-dollar-circle-line text-[#FF9F1C]"></i>
                  Dinheiro
                </span>
              </div>
              <p className="text-xs text-gray-400">© 2025 Gil Lanches. Todos os direitos reservados.</p>
            </div>
            <div className="text-center mt-3">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-[#FF9F1C] transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
